import React, { useState, useEffect } from 'react';
import { Session, Component, Feature, PRFeedback, PRFeedbackTypeItem } from '../../../../types';
import { formatDate } from '../../../../utils/date';
import { apiRequest } from '../../../../utils/api';

interface PRFeedbackValueTabProps {
  sessions: Session[];
  components: Component[];
  features: Feature[];
  selectedMilestone: string;
  filterSessionsByMilestone: (sessions: Session[], milestone: string) => Session[];
  filterComponentsByMilestone: (components: Component[], milestone: string) => Component[];
}

const PRFeedbackValueTab: React.FC<PRFeedbackValueTabProps> = ({
  sessions,
  components,
  features,
  selectedMilestone,
  filterSessionsByMilestone,
  filterComponentsByMilestone
}) => {
  // Configurable types from database
  const [commentTypes, setCommentTypes] = useState<PRFeedbackTypeItem[]>([]);
  const [severities, setSeverities] = useState<PRFeedbackTypeItem[]>([]);
  const [impacts, setImpacts] = useState<PRFeedbackTypeItem[]>([]);
  const [typesLoading, setTypesLoading] = useState(true);
  
  // Fetch configurable types
  useEffect(() => {
    const fetchTypes = async () => {
      setTypesLoading(true);
      try {
        const [commentTypesData, severitiesData, impactsData] = await Promise.all([
          apiRequest<PRFeedbackTypeItem[]>('/api/prfeedback-comment-types', {}, { useAuth: true }),
          apiRequest<PRFeedbackTypeItem[]>('/api/prfeedback-severities', {}, { useAuth: true }),
          apiRequest<PRFeedbackTypeItem[]>('/api/prfeedback-impacts', {}, { useAuth: true })
        ]);
        
        setCommentTypes((commentTypesData || []).sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0)));
        setSeverities((severitiesData || []).sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0)));
        setImpacts((impactsData || []).sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0)));
      } catch (error) {
        console.error('Error fetching PR feedback types:', error);
      } finally {
        setTypesLoading(false);
      }
    };
    
    fetchTypes();
  }, []);

  // Filter sessions and components by milestone
  const milestoneFilteredSessions = filterSessionsByMilestone(sessions, selectedMilestone);
  const milestoneFilteredComponents = filterComponentsByMilestone(components, selectedMilestone);
  const completedSessions = milestoneFilteredSessions.filter(s => s.status === 'completed');

  // Get all PR feedback from completed sessions
  const allPRFeedbacks: PRFeedback[] = [];
  completedSessions.forEach(session => {
    if (session.prFeedbacks && session.prFeedbacks.length > 0) {
      allPRFeedbacks.push(...session.prFeedbacks);
    }
  });

  // Get unique PR feedback by commentUrl (deduplicated)
  const uniquePRFeedbackUrls = new Set<string>();
  allPRFeedbacks.forEach(feedback => {
    if (feedback.commentUrl && feedback.commentUrl.trim()) {
      uniquePRFeedbackUrls.add(feedback.commentUrl.trim());
    }
  });

  // Calculate Early Catch Rate
  // PR Feedback: Found during PR review (not feature freeze)
  const prFeedbackSessions = completedSessions.filter(s => {
    if (!s.prFeedbacks || s.prFeedbacks.length === 0) return false;
    return s.sessionType !== 'Feature Freeze Regression' &&
           s.sessionType !== 'Release Candidate Regression' &&
           s.sessionType !== 'Release Exploratory';
  });

  const prFeedbackCount = prFeedbackSessions.reduce((sum, s) => sum + (s.prFeedbacks?.length || 0), 0);

  // Feature Freeze Bugs: Found during feature freeze/regression
  // Only count unique bug URLs (same as "Unique Bugs" calculation)
  // Don't count componentStatuses.bugged separately as those are already represented in bugUrls
  const featureFreezeBugSessions = completedSessions.filter(s => {
    if (s.sessionType !== 'Feature Freeze Regression' &&
        s.sessionType !== 'Release Candidate Regression' &&
        s.sessionType !== 'Release Exploratory') {
      return false;
    }
    // Only count sessions that have bugUrls (bugs added via bug modal)
    return s.bugUrls && s.bugUrls.length > 0;
  });

  // Count unique bug URLs from feature freeze sessions (deduplicated by URL)
  const featureFreezeBugUrls = new Set<string>();
  featureFreezeBugSessions.forEach(s => {
    if (s.bugUrls && s.bugUrls.length > 0) {
      s.bugUrls.forEach(bu => {
        if (typeof bu === 'string') return;
        if (bu.url && bu.url.trim()) {
          featureFreezeBugUrls.add(bu.url.trim());
        }
      });
    }
  });

  const featureFreezeBugCount = featureFreezeBugUrls.size;

  const earlyCatchRate = (prFeedbackCount + featureFreezeBugCount) > 0
    ? (prFeedbackCount / (prFeedbackCount + featureFreezeBugCount)) * 100
    : 0;

  // Production Blockers Prevented - find all feedback with impact that matches "production-blocker" name
  // First try to find by exact name match, otherwise check all impacts to see if any match
  const productionBlockerImpactName = impacts.find(imp => imp.name === 'production-blocker')?.name || 
                                     impacts.find(imp => imp.name.toLowerCase().includes('production') && imp.name.toLowerCase().includes('blocker'))?.name ||
                                     null;
  
  // If we have a specific impact name, use it; otherwise, check all feedback and see if impact matches any impact type
  const productionBlockers = productionBlockerImpactName
    ? allPRFeedbacks.filter(fb => fb.impact === productionBlockerImpactName)
    : allPRFeedbacks.filter(fb => {
        // Fallback: check if impact name contains "production" and "blocker" (case insensitive)
        if (!fb.impact) return false;
        const impactLower = fb.impact.toLowerCase();
        return impactLower.includes('production') && impactLower.includes('blocker');
      });
  
  const uniqueProductionBlockers = new Set<string>();
  productionBlockers.forEach(fb => {
    if (fb.commentUrl && fb.commentUrl.trim()) {
      uniqueProductionBlockers.add(fb.commentUrl.trim());
    }
  });

  // PR Feedback Caught Before Merge (total unique)
  const totalPRFeedbackCaught = uniquePRFeedbackUrls.size;

  // Design Improvements - find all feedback with comment type that matches "design-feedback" name
  const designFeedbackTypeName = commentTypes.find(ct => ct.name === 'design-feedback')?.name ||
                                 commentTypes.find(ct => ct.name.toLowerCase().includes('design'))?.name ||
                                 null;
  
  const designFeedbacks = designFeedbackTypeName
    ? allPRFeedbacks.filter(fb => fb.commentType === designFeedbackTypeName)
    : allPRFeedbacks.filter(fb => {
        // Fallback: check if comment type name contains "design" (case insensitive)
        if (!fb.commentType) return false;
        const typeLower = fb.commentType.toLowerCase();
        return typeLower.includes('design');
      });
  
  const uniqueDesignFeedbacks = new Set<string>();
  designFeedbacks.forEach(fb => {
    if (fb.commentUrl && fb.commentUrl.trim()) {
      uniqueDesignFeedbacks.add(fb.commentUrl.trim());
    }
  });

  // Quality Hotspots - Components with most PR feedback
  const componentPRFeedbackCounts = new Map<string, { component: Component; feature: Feature; count: number; feedbacks: PRFeedback[] }>();
  
  allPRFeedbacks.forEach(feedback => {
    if (!feedback.componentId) return;
    const component = milestoneFilteredComponents.find(c => c.id === feedback.componentId);
    if (!component) return;
    
    const feature = features.find(f => f.id === feedback.featureId);
    if (!feature) return;

    if (!componentPRFeedbackCounts.has(feedback.componentId)) {
      componentPRFeedbackCounts.set(feedback.componentId, {
        component,
        feature,
        count: 0,
        feedbacks: []
      });
    }
    
    const entry = componentPRFeedbackCounts.get(feedback.componentId)!;
    // Only count unique commentUrls per component
    if (!entry.feedbacks.some(fb => fb.commentUrl === feedback.commentUrl)) {
      entry.count++;
      entry.feedbacks.push(feedback);
    }
  });

  const qualityHotspots = Array.from(componentPRFeedbackCounts.values())
    .sort((a, b) => b.count - a.count)
    .slice(0, 20); // Top 20

  // Quality Hotspots - Features with most PR feedback
  const featurePRFeedbackCounts = new Map<string, { feature: Feature; count: number; feedbacks: PRFeedback[] }>();
  
  allPRFeedbacks.forEach(feedback => {
    if (!feedback.featureId) return;
    const feature = features.find(f => f.id === feedback.featureId);
    if (!feature) return;

    if (!featurePRFeedbackCounts.has(feedback.featureId)) {
      featurePRFeedbackCounts.set(feedback.featureId, {
        feature,
        count: 0,
        feedbacks: []
      });
    }
    
    const entry = featurePRFeedbackCounts.get(feedback.featureId)!;
    // Only count unique commentUrls per feature
    if (!entry.feedbacks.some(fb => fb.commentUrl === feedback.commentUrl)) {
      entry.count++;
      entry.feedbacks.push(feedback);
    }
  });

  const featureQualityHotspots = Array.from(featurePRFeedbackCounts.values())
    .sort((a, b) => b.count - a.count)
    .slice(0, 20); // Top 20

  // Sub-tab state
  const [activeSubTab, setActiveSubTab] = useState<'overview' | 'breakdown' | 'hotspots'>('overview');
  
  // Modal state
  const [selectedFeedback, setSelectedFeedback] = useState<PRFeedback[] | null>(null);
  const [modalTitle, setModalTitle] = useState<string>('');

  // Breakdown by Comment Type - dynamically built from configurable types
  const feedbackByType: Record<string, PRFeedback[]> = {};
  const uniqueByType: Record<string, Set<string>> = {};
  
  commentTypes.forEach(ct => {
    feedbackByType[ct.name] = allPRFeedbacks.filter(fb => fb.commentType === ct.name);
    uniqueByType[ct.name] = new Set<string>();
    feedbackByType[ct.name].forEach(fb => {
      if (fb.commentUrl && fb.commentUrl.trim()) {
        uniqueByType[ct.name].add(fb.commentUrl.trim());
      }
    });
  });

  // Breakdown by Impact - dynamically built from configurable types
  const feedbackByImpact: Record<string, PRFeedback[]> = {};
  const uniqueByImpact: Record<string, Set<string>> = {};
  
  impacts.forEach(imp => {
    feedbackByImpact[imp.name] = allPRFeedbacks.filter(fb => fb.impact === imp.name);
    uniqueByImpact[imp.name] = new Set<string>();
    feedbackByImpact[imp.name].forEach(fb => {
      if (fb.commentUrl && fb.commentUrl.trim()) {
        uniqueByImpact[imp.name].add(fb.commentUrl.trim());
      }
    });
  });

  // Breakdown by Severity - dynamically built from configurable types
  const feedbackBySeverity: Record<string, PRFeedback[]> = {};
  const uniqueBySeverity: Record<string, Set<string>> = {};
  
  severities.forEach(sev => {
    feedbackBySeverity[sev.name] = allPRFeedbacks.filter(fb => fb.severity === sev.name);
    uniqueBySeverity[sev.name] = new Set<string>();
    feedbackBySeverity[sev.name].forEach(fb => {
      if (fb.commentUrl && fb.commentUrl.trim()) {
        uniqueBySeverity[sev.name].add(fb.commentUrl.trim());
      }
    });
  });

  // Helper functions for labels - use actual type names from database
  const getCommentTypeLabel = (type: string) => {
    const typeItem = commentTypes.find(ct => ct.name === type);
    return typeItem ? typeItem.name : type;
  };

  const getSeverityLabel = (severity: string) => {
    const severityItem = severities.find(sev => sev.name === severity);
    return severityItem ? severityItem.name : severity;
  };

  const getImpactLabel = (impact: string) => {
    const impactItem = impacts.find(imp => imp.name === impact);
    return impactItem ? impactItem.name : impact;
  };

  // Show loading state while types are being fetched
  if (typesLoading) {
    return (
      <div>
        <h2 className="insights-section-title">PR Feedback Value</h2>
        <p className="insights-section-subtitle">
          Loading PR feedback types...
        </p>
        <div className="card" style={{ padding: '2rem', textAlign: 'center' }}>
          <div>Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h2 className="insights-section-title">PR Feedback Value</h2>
      <p className="insights-section-subtitle">
        Insights on the value QA provides through PR feedback{selectedMilestone === 'all-time' ? ' (all time)' : ` for milestone ${selectedMilestone}`}.
      </p>

      {/* Sub-tabs */}
      <div className="tabs insights-subtab-tabs">
        <button
          className={`tab ${activeSubTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('overview')}
        >
          Overview
        </button>
        <button
          className={`tab ${activeSubTab === 'breakdown' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('breakdown')}
        >
          Breakdown
        </button>
        <button
          className={`tab ${activeSubTab === 'hotspots' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('hotspots')}
        >
          Quality Hotspots
        </button>
      </div>

      {/* Overview Sub-tab */}
      {activeSubTab === 'overview' && (
        <div>
          {/* Summary Statistics */}
      <div className="insights-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', marginBottom: '2rem' }}>
        <div className="card insights-card">
          <div className="insights-stat-label">Early Catch Rate</div>
          <div className={`insights-stat-value ${earlyCatchRate >= 50 ? 'insights-stat-value-success' : earlyCatchRate >= 30 ? 'insights-stat-value-warning' : 'insights-stat-value-danger'}`} style={{ fontSize: '2rem' }}>
            {earlyCatchRate.toFixed(1)}%
          </div>
          <div className="subtitle" style={{ fontSize: '0.75rem', marginTop: '0.25rem', color: 'var(--text-secondary)' }}>
            {prFeedbackCount} PR feedback vs {featureFreezeBugCount} feature freeze bugs
          </div>
        </div>

        <div className="card insights-card">
          <div className="insights-stat-label">Production Blockers Prevented</div>
          <div className="insights-stat-value insights-stat-value-danger" style={{ fontSize: '2rem' }}>
            {uniqueProductionBlockers.size}
          </div>
          <div className="subtitle" style={{ fontSize: '0.75rem', marginTop: '0.25rem', color: 'var(--text-secondary)' }}>
            Critical issues caught before merge
          </div>
        </div>

        <div className="card insights-card">
          <div className="insights-stat-label">PR Feedback Caught Before Merge</div>
          <div className="insights-stat-value insights-stat-value-primary" style={{ fontSize: '2rem' }}>
            {totalPRFeedbackCaught}
          </div>
          <div className="subtitle" style={{ fontSize: '0.75rem', marginTop: '0.25rem', color: 'var(--text-secondary)' }}>
            Total unique feedback items
          </div>
        </div>

        <div className="card insights-card">
          <div className="insights-stat-label">Design Improvements</div>
          <div className="insights-stat-value insights-stat-value-success" style={{ fontSize: '2rem' }}>
            {uniqueDesignFeedbacks.size}
          </div>
          <div className="subtitle" style={{ fontSize: '0.75rem', marginTop: '0.25rem', color: 'var(--text-secondary)' }}>
            UX improvements before release
          </div>
        </div>
      </div>
        </div>
      )}

      {/* Quality Hotspots Sub-tab */}
      {activeSubTab === 'hotspots' && (
        <div>
          {/* Quality Hotspots - Components */}
          <div style={{ marginBottom: '2rem' }}>
            <h3 className="insights-subtab-title" style={{ marginBottom: '1rem' }}>Quality Hotspots - Components</h3>
        <p className="insights-section-subtitle" style={{ marginBottom: '1rem', fontSize: '0.875rem' }}>
          Components with the most PR feedback (indicating areas that may need more attention or have higher complexity).
        </p>
        
        {qualityHotspots.length === 0 ? (
          <div className="card insights-empty-state">
            <div className="insights-empty-icon">💬</div>
            <h3 className="insights-empty-title">No PR Feedback Found</h3>
            <p className="insights-empty-text">No PR feedback has been recorded for the selected milestone.</p>
          </div>
        ) : (
          <div className="card" style={{ overflow: 'hidden' }}>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {qualityHotspots.map((hotspot, index) => (
                <div
                  key={hotspot.component.id}
                  style={{
                    padding: '1rem',
                    borderBottom: index < qualityHotspots.length - 1 ? '1px solid var(--grid-line)' : 'none',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 500, color: 'var(--text-primary)', marginBottom: '0.25rem' }}>
                      {hotspot.component.name}
                    </div>
                    <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                      Feature: {hotspot.feature.name}
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.25rem' }}>
                    <span className="badge badge-primary" style={{ fontSize: '0.875rem', padding: '0.5rem 0.75rem' }}>
                      {hotspot.count} PR Feedback
                    </span>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                      {hotspot.feedbacks.length} unique comment{hotspot.feedbacks.length !== 1 ? 's' : ''}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Quality Hotspots - Features */}
      <div>
        <h3 className="insights-subtab-title" style={{ marginBottom: '1rem' }}>Quality Hotspots - Features</h3>
        <p className="insights-section-subtitle" style={{ marginBottom: '1rem', fontSize: '0.875rem' }}>
          Features with the most PR feedback (indicating areas that may need more attention or have higher complexity).
        </p>
        
        {featureQualityHotspots.length === 0 ? (
          <div className="card insights-empty-state">
            <div className="insights-empty-icon">💬</div>
            <h3 className="insights-empty-title">No PR Feedback Found</h3>
            <p className="insights-empty-text">No PR feedback has been recorded for the selected milestone.</p>
          </div>
        ) : (
          <div className="card" style={{ overflow: 'hidden' }}>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {featureQualityHotspots.map((hotspot, index) => (
                <div
                  key={hotspot.feature.id}
                  style={{
                    padding: '1rem',
                    borderBottom: index < featureQualityHotspots.length - 1 ? '1px solid var(--grid-line)' : 'none',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 500, color: 'var(--text-primary)', marginBottom: '0.25rem' }}>
                      {hotspot.feature.name}
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.25rem' }}>
                    <span className="badge badge-primary" style={{ fontSize: '0.875rem', padding: '0.5rem 0.75rem' }}>
                      {hotspot.count} PR Feedback
                    </span>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                      {hotspot.feedbacks.length} unique comment{hotspot.feedbacks.length !== 1 ? 's' : ''}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        </div>
      </div>
      )}

      {/* Breakdown Sub-tab */}
      {activeSubTab === 'breakdown' && (
        <div>
          {/* Comment Type Section */}
          <div style={{ marginBottom: '2rem' }}>
            <h3 className="insights-subtab-title" style={{ marginBottom: '1rem' }}>By Comment Type</h3>
            <p className="insights-section-subtitle" style={{ marginBottom: '1.5rem', fontSize: '0.875rem' }}>
              Breakdown of PR feedback by the type of comment provided.
            </p>
            
            <div className="insights-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', marginBottom: '2rem' }}>
              {commentTypes.length === 0 ? (
                <div className="card" style={{ padding: '2rem', textAlign: 'center', gridColumn: '1 / -1' }}>
                  <p style={{ color: 'var(--text-secondary)' }}>No comment types configured. Please configure comment types in Settings → Parameters.</p>
                </div>
              ) : (
                commentTypes.map((ct, index) => {
                const feedbacks = feedbackByType[ct.name] || [];
                const uniqueCount = uniqueByType[ct.name]?.size || 0;
                const colorClasses = ['insights-stat-value-danger', 'insights-stat-value-success', 'insights-stat-value-warning', 'insights-stat-value-primary'];
                const colorClass = colorClasses[index % colorClasses.length];
                
                return (
                  <div 
                    key={ct.id}
                    className="card insights-card" 
                    style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                    onClick={() => {
                      setSelectedFeedback(feedbacks);
                      setModalTitle(getCommentTypeLabel(ct.name));
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <div className="insights-stat-label">{getCommentTypeLabel(ct.name)}</div>
                    <div className={`insights-stat-value ${colorClass}`} style={{ fontSize: '2rem' }}>
                      {uniqueCount}
                    </div>
                    <div className="subtitle" style={{ fontSize: '0.75rem', marginTop: '0.25rem', color: 'var(--text-secondary)' }}>
                      {feedbacks.length} total feedback
                    </div>
                  </div>
                );
              }))}
            </div>
          </div>

          {/* Impact Section */}
          <div style={{ marginBottom: '2rem' }}>
            <h3 className="insights-subtab-title" style={{ marginBottom: '1rem' }}>By Impact</h3>
            <p className="insights-section-subtitle" style={{ marginBottom: '1.5rem', fontSize: '0.875rem' }}>
              Breakdown of PR feedback by the impact level of the issue.
            </p>
            
            <div className="insights-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', marginBottom: '2rem' }}>
              {impacts.length === 0 ? (
                <div className="card" style={{ padding: '2rem', textAlign: 'center', gridColumn: '1 / -1' }}>
                  <p style={{ color: 'var(--text-secondary)' }}>No impact types configured. Please configure impact types in Settings → Parameters.</p>
                </div>
              ) : (
                impacts.map((imp, index) => {
                const feedbacks = feedbackByImpact[imp.name] || [];
                const uniqueCount = uniqueByImpact[imp.name]?.size || 0;
                const colorClasses = ['insights-stat-value-danger', 'insights-stat-value-warning', 'insights-stat-value-primary', 'insights-stat-value-success'];
                const colorClass = colorClasses[index % colorClasses.length];
                
                return (
                  <div 
                    key={imp.id}
                    className="card insights-card" 
                    style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                    onClick={() => {
                      setSelectedFeedback(feedbacks);
                      setModalTitle(getImpactLabel(imp.name));
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <div className="insights-stat-label">{getImpactLabel(imp.name)}</div>
                    <div className={`insights-stat-value ${colorClass}`} style={{ fontSize: '2rem' }}>
                      {uniqueCount}
                    </div>
                    <div className="subtitle" style={{ fontSize: '0.75rem', marginTop: '0.25rem', color: 'var(--text-secondary)' }}>
                      {feedbacks.length} total feedback
                    </div>
                  </div>
                );
              }))}
            </div>
          </div>

          {/* Severity Section */}
          <div>
            <h3 className="insights-subtab-title" style={{ marginBottom: '1rem' }}>By Severity</h3>
            <p className="insights-section-subtitle" style={{ marginBottom: '1.5rem', fontSize: '0.875rem' }}>
              Breakdown of PR feedback by severity level.
            </p>
            
            <div className="insights-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', marginBottom: '2rem' }}>
              {severities.length === 0 ? (
                <div className="card" style={{ padding: '2rem', textAlign: 'center', gridColumn: '1 / -1' }}>
                  <p style={{ color: 'var(--text-secondary)' }}>No severity types configured. Please configure severity types in Settings → Parameters.</p>
                </div>
              ) : (
                severities.map((sev, index) => {
                const feedbacks = feedbackBySeverity[sev.name] || [];
                const uniqueCount = uniqueBySeverity[sev.name]?.size || 0;
                const colorClasses = ['insights-stat-value-danger', 'insights-stat-value-warning', 'insights-stat-value-primary', 'insights-stat-value-success'];
                const colorClass = colorClasses[index % colorClasses.length];
                
                return (
                  <div 
                    key={sev.id}
                    className="card insights-card" 
                    style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                    onClick={() => {
                      setSelectedFeedback(feedbacks);
                      setModalTitle(getSeverityLabel(sev.name));
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <div className="insights-stat-label">{getSeverityLabel(sev.name)}</div>
                    <div className={`insights-stat-value ${colorClass}`} style={{ fontSize: '2rem' }}>
                      {uniqueCount}
                    </div>
                    <div className="subtitle" style={{ fontSize: '0.75rem', marginTop: '0.25rem', color: 'var(--text-secondary)' }}>
                      {feedbacks.length} total feedback
                    </div>
                  </div>
                );
              }))}
            </div>
          </div>
        </div>
      )}

      {/* PR Feedback Detail Modal */}
      {selectedFeedback && (
        <div
          className="modal-overlay"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
            padding: '2rem',
            overflow: 'hidden',
            pointerEvents: 'auto'
          }}
          onClick={() => {
            setSelectedFeedback(null);
            setModalTitle('');
          }}
          onWheel={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.preventDefault()}
        >
          <div
            className="card"
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: '900px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: 'var(--surface)',
            }}
          >
            {/* Modal Header */}
            <div
              style={{
                padding: '1.5rem',
                borderBottom: '1px solid var(--grid-line)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                position: 'sticky',
                top: 0,
                backgroundColor: 'var(--surface)',
                zIndex: 10,
              }}
            >
              <h2 style={{ margin: 0, fontSize: '1.5rem', color: 'var(--text-primary)' }}>
                {modalTitle} - PR Feedback Details
              </h2>
              <button
                className="btn btn-ghost btn-icon"
                onClick={() => {
                  setSelectedFeedback(null);
                  setModalTitle('');
                }}
                style={{ fontSize: '1.5rem', padding: '0.5rem' }}
              >
                ×
              </button>
            </div>

            {/* Modal Content */}
            <div style={{ padding: '1.5rem', overflowY: 'auto', flex: 1 }}>
              {selectedFeedback.length === 0 ? (
                <div className="text-center p-lg hint">
                  No PR feedback found in this category.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {selectedFeedback.map((feedback, index) => {
                    const component = milestoneFilteredComponents.find(c => c.id === feedback.componentId);
                    const feature = features.find(f => f.id === feedback.featureId);
                    
                    return (
                      <div
                        key={`${feedback.id}-${index}`}
                        style={{
                          padding: '1rem',
                          border: '1px solid var(--grid-line)',
                          borderRadius: '6px',
                          backgroundColor: 'var(--surface-alt)',
                        }}
                      >
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                          {/* PR URL */}
                          <div>
                            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem', fontWeight: '500' }}>
                              PR Link:
                            </div>
                            <a
                              href={feedback.prUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                color: 'var(--primary)',
                                textDecoration: 'none',
                                fontSize: '0.875rem',
                                wordBreak: 'break-all',
                                fontWeight: '500'
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                              onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
                            >
                              {feedback.prUrl}
                            </a>
                          </div>

                          {/* Comment URL */}
                          <div>
                            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem', fontWeight: '500' }}>
                              Comment Link:
                            </div>
                            <a
                              href={feedback.commentUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                color: 'var(--primary)',
                                textDecoration: 'none',
                                fontSize: '0.875rem',
                                wordBreak: 'break-all',
                                fontWeight: '500'
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                              onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
                            >
                              {feedback.commentUrl}
                            </a>
                          </div>

                          {/* Metadata */}
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center' }}>
                            <span className="badge badge-primary" style={{ fontSize: '0.75rem' }}>
                              {getCommentTypeLabel(feedback.commentType)}
                            </span>
                            <span className="badge badge-danger" style={{ fontSize: '0.75rem' }}>
                              {getSeverityLabel(feedback.severity)}
                            </span>
                            <span className="badge badge-warning" style={{ fontSize: '0.75rem' }}>
                              {getImpactLabel(feedback.impact)}
                            </span>
                          </div>

                          {/* Component and Feature */}
                          <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                            <div>
                              <strong>Component:</strong> {component?.name || 'Unknown'}
                            </div>
                            <div>
                              <strong>Feature:</strong> {feature?.name || 'Unknown'}
                            </div>
                          </div>

                          {/* Description */}
                          {feedback.description && (
                            <div style={{
                              padding: '0.75rem',
                              backgroundColor: 'var(--surface)',
                              borderRadius: '4px',
                              fontSize: '0.875rem',
                              color: 'var(--text-primary)',
                              border: '1px solid var(--grid-line)'
                            }}>
                              {feedback.description}
                            </div>
                          )}

                          {/* Created Date */}
                          <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                            Created: {formatDate(feedback.createdAt, 'MMM dd, yyyy h:mm a')}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PRFeedbackValueTab;
